export type AuthSession = {
  session: { id: string; userId: string };
  user: { id: string; email: string | null; name: string | null };
};

function toSession(value: unknown): AuthSession | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const sessionValue = record.session;
  const userValue = record.user;
  if (!sessionValue || typeof sessionValue !== "object") return null;
  if (!userValue || typeof userValue !== "object") return null;
  const session = sessionValue as Record<string, unknown>;
  const user = userValue as Record<string, unknown>;
  if (typeof session.id !== "string" || typeof session.userId !== "string") return null;
  if (typeof user.id !== "string") return null;
  return {
    session: { id: session.id, userId: session.userId },
    user: {
      id: user.id,
      email: typeof user.email === "string" ? user.email : null,
      name: typeof user.name === "string" ? user.name : null,
    },
  };
}

/** Mensagens de erro de auth em pt-BR, claras pro usuário leigo. Mapeadas pelo
 *  CÓDIGO do Better Auth (estável); fallback por status HTTP. Por segurança,
 *  "usuário não existe" e "senha errada" caem na MESMA mensagem (não revela se
 *  o e-mail tem conta). */
const AUTH_ERROR_PT: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "E-mail ou senha incorretos. Confira e tente de novo.",
  USER_NOT_FOUND: "E-mail ou senha incorretos. Confira e tente de novo.",
  ACCOUNT_NOT_FOUND: "E-mail ou senha incorretos. Confira e tente de novo.",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "E-mail ou senha incorretos. Confira e tente de novo.",
  INVALID_PASSWORD: "Senha incorreta. Confira e tente de novo.",
  EMAIL_NOT_VERIFIED: "Seu e-mail ainda não foi confirmado. Fale com o admin da sua empresa.",
  TOO_MANY_REQUESTS: "Muitas tentativas seguidas. Espere um minuto e tente de novo.",
  PASSWORD_TOO_SHORT: "A senha precisa ter pelo menos 8 caracteres.",
  PASSWORD_TOO_LONG: "A senha é longa demais.",
  USER_ALREADY_EXISTS: "Já existe uma conta com esse e-mail.",
  INVALID_EMAIL: "Digite um e-mail válido.",
};

function translateAuthError(code: string | undefined, status: number, message?: string): string {
  if (code && AUTH_ERROR_PT[code]) return AUTH_ERROR_PT[code]!;
  // Fallback por texto: o Better Auth nem sempre manda o `code` esperado no
  // corpo, então também reconhecemos "conta já existe" pela mensagem (era o caso
  // que caía no genérico "Confira os dados" ao recriar uma conta que já existe).
  if (message && /already\s*exist|já existe/i.test(message)) {
    return AUTH_ERROR_PT.USER_ALREADY_EXISTS!;
  }
  if (status === 401 || status === 403) return "E-mail ou senha incorretos. Confira e tente de novo.";
  if (status === 429) return "Muitas tentativas seguidas. Espere um minuto e tente de novo.";
  if (status === 400 || status === 422) return "Confira os dados e tente de novo.";
  if (status >= 500) return "Tivemos um problema no servidor. Tente de novo em instantes.";
  return "Não foi possível concluir. Tente de novo.";
}

async function authPost(path: string, body: Record<string, unknown>) {
  let res: Response;
  try {
    res = await fetch(`/api/auth${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Sem conexão com o servidor. Verifique sua internet e tente de novo.");
  }
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    // Better Auth devolve {message, code} (às vezes aninhado em .error).
    const root = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
    const errObj =
      root.error && typeof root.error === "object" ? (root.error as Record<string, unknown>) : root;
    const code = typeof errObj.code === "string" ? errObj.code : undefined;
    const message = typeof errObj.message === "string" ? errObj.message : undefined;
    throw new Error(translateAuthError(code, res.status, message));
  }
  return payload;
}

export const authApi = {
  getSession: async (): Promise<AuthSession | null> => {
    const res = await fetch("/api/auth/get-session", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (res.status === 401) return null;
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(`Failed to load session (${res.status})`);
    }
    const direct = toSession(payload);
    if (direct) return direct;
    const nested =
      payload && typeof payload === "object"
        ? toSession((payload as Record<string, unknown>).data)
        : null;
    return nested;
  },

  signInEmail: async (input: { email: string; password: string }) => {
    await authPost("/sign-in/email", input);
  },

  signUpEmail: async (input: { name: string; email: string; password: string }) => {
    await authPost("/sign-up/email", input);
  },

  signOut: async () => {
    await authPost("/sign-out", {});
  },

  /** Retorna info do user logado + flag isAdmin (pra mostrar /admin/users) +
   *  deploymentMode (pra UI liberar ferramentas só de dev, ex: reset de QA). */
  me: async (): Promise<{
    authenticated: boolean;
    isAdmin: boolean;
    email?: string | null;
    deploymentMode?: string;
  }> => {
    const res = await fetch("/api/auth/me", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return { authenticated: false, isAdmin: false };
    return res.json();
  },

  changePassword: async (input: {
    currentPassword: string;
    newPassword: string;
  }) => {
    await authPost("/change-password", input);
  },

  updateUser: async (input: { name?: string }) => {
    await authPost("/update-user", input);
  },
};
